import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import type { CalculateFees, OrderFeeLine } from '@modules/orders'
import { formatAed } from '@shared/utils'
import { DEFAULT_CALCULATOR_AMOUNT } from '../constants/propertyDefaults'
import {
  CALCULATOR_FEE_INFO,
  type CalculatorFeeInfoKey,
  type FeeInfo,
} from '../constants/calculatorFeeInfo'
import type { PropertyDetail, PropertyDetailFee } from '../types/propertyDetailTypes'
import { netYieldOf } from './propertyFinancialUtils'

// Pure builders for the right rail's investment calculator: its inputs, the
// pre-quote fee estimate, the return projection and the ⓘ copy per result.

const PERCENT_BASE = 100

// ── Investment calculator ────────────────────────────────────────────────

export interface CalculatorConfig {
  tokenPrice: number
  minInvestment: number
  /** Everything still on sale, in AED. */
  maxInvestment: number
  defaultAmount: number
  netYieldPct: number
  appreciationPct: number
  /** Sum of the investor's percentage fees at purchase — the pre-quote estimate. */
  feeRatePct: number
}

const PRIMARY_SALE_STAGE = 'primary_sell'
const INVESTOR_PAYER = 'investor'

/** Percentage fees the investor pays on a primary purchase ("2%" + "2%" → 4). */
export const estimateFeeRatePct = (fees: PropertyDetailFee[]): number =>
  fees
    .filter((fee) => fee.lifecycleStage === PRIMARY_SALE_STAGE && fee.paidBy === INVESTOR_PAYER)
    .reduce((sum, fee) => {
      const rate = Number(fee.rate?.replace('%', ''))
      return Number.isFinite(rate) ? sum + rate : sum
    }, 0)

export const buildCalculatorConfig = (data: PropertyDetail | null): CalculatorConfig => {
  const tokenPrice = data?.tokenPrice ?? 0
  const minInvestment = Math.max(data?.minInvestmentAmount ?? 0, tokenPrice)
  const maxInvestment = (data?.tokensAvailable ?? 0) * tokenPrice
  const upper = maxInvestment > 0 ? maxInvestment : DEFAULT_CALCULATOR_AMOUNT
  return {
    tokenPrice,
    minInvestment,
    maxInvestment,
    defaultAmount: Math.min(Math.max(DEFAULT_CALCULATOR_AMOUNT, minInvestment), upper),
    netYieldPct: (data && netYieldOf(data)) ?? 0,
    appreciationPct: data?.expectedCapitalAppreciation ?? 0,
    feeRatePct: estimateFeeRatePct(data?.fees ?? []),
  }
}

export interface ReturnProjection {
  rentalIncome: number
  appreciation: number
  expectedValue: number
  /** Appreciation over the horizon as a % of the amount invested. */
  appreciationPct: number
  /** Rent over the horizon as a % of the amount invested. */
  rentalIncomePct: number
  /** Total return over the horizon as a % of the amount invested. */
  roiPct: number
}

/**
 * Rent accrues linearly at the net yield; the property value compounds at the
 * appreciation rate. Expected value = amount + rent + appreciation.
 */
export const projectReturns = (
  amount: number,
  years: number,
  netYieldPct: number,
  appreciationPct: number,
): ReturnProjection => {
  const rentalIncome = (amount * netYieldPct * years) / PERCENT_BASE
  const appreciation = amount * ((1 + appreciationPct / PERCENT_BASE) ** years - 1)
  const gain = rentalIncome + appreciation
  const pctOfAmount = (value: number) => (amount > 0 ? (value / amount) * PERCENT_BASE : 0)
  return {
    rentalIncome,
    appreciation,
    expectedValue: amount + gain,
    appreciationPct: pctOfAmount(appreciation),
    rentalIncomePct: pctOfAmount(rentalIncome),
    roiPct: pctOfAmount(gain),
  }
}

/**
 * ⓘ explainers under the calculator's three result stats — the app's copy
 * verbatim. The app sheets are a single paragraph (no tinted note, no
 * formula), so it renders as the sheet's paragraph alone.
 */
export const PROJECTION_SHEETS: Record<'appreciation' | 'roi' | 'rental', InfoSheetContent> = {
  appreciation: {
    title: 'Appreciation',
    description:
      "The increase in this property's value over your selected time period, based on projected market growth. This is an estimated price gain only.",
  },
  roi: {
    title: 'Estimated ROI',
    description:
      "Your estimated total return on this investment over the selected period, combining both the property's value growth and the rental income you're expected to receive.",
  },
  rental: {
    title: 'Rental Income',
    description:
      "The rental earnings this investment is expected to generate over the selected period, based on the property's net rental yield, paid out to your wallet.",
  },
}

// ── Total cost: fee lines ────────────────────────────────────────────────

// The app's Total cost well (investor-app TotalCostBreakdown / FeeBreakdownCard):
// DLD, Title Deed and NOC listed in full, then the platform fee net of VAT
// with its VAT on a line of its own, then every other charge folded into one
// "Acquisition Cost" line whose caret opens a sheet itemising them. Each
// line carries the ⓘ copy the app gives it.

export interface CalculatorFeeRow {
  key: string
  /** "DLD Fee (2%)" — the rate is appended when the fee has one. */
  label: string
  /** Formatted amount, e.g. "AED 40.01". */
  value: string
  /** ⓘ explainer, where the app has one for this charge. */
  info?: FeeInfo
}

export interface AcquisitionCostBreakdown {
  /** "Fees including 5% VAT on each" — or "Fees" when none carry VAT. */
  sectionTitle: string
  rows: CalculatorFeeRow[]
}

export interface CalculatorFeeLine extends CalculatorFeeRow {
  /** Set on the Acquisition Cost line — its caret opens these rows. */
  acquisition?: AcquisitionCostBreakdown
}

const ACQUISITION_COST_KEY = 'acquisitionCost'
const ACQUISITION_COST_LABEL = 'Acquisition Cost'
const PLATFORM_FEE_VAT_KEY = 'platformFeeVat'
const PLATFORM_FEE_VAT_LABEL = 'VAT on Platform Fee'
const PLATFORM_FEE_TOKEN = 'platformfee'
/** What a charge of nothing states in the Acquisition Cost sheet. */
const NIL_AMOUNT = 'AED -'
const MAX_RATE_DECIMALS = 2

/** Payment-gateway charges read as AED 0 and say nothing about the property. */
const HIDDEN_FEE_TOKENS = ['pgconvenience', 'paymentgateway']

/** Charges listed inline under Total cost, in the order drawn. */
const INLINE_ORDER: readonly (readonly string[])[] = [['dld'], ['titledeed'], ['noc']]

/** The Acquisition Cost sheet's order; unmatched charges follow in quote order. */
const ACQUISITION_ORDER: readonly (readonly string[])[] = [
  ['brokerage'],
  ['trustee'],
  ['valuation'],
  ['conveyancing'],
  ['utilit'],
  ['furnish', 'furniture'],
  ['renovation', 'refurbish'],
  ['maintenance'],
  ['insurance'],
  ['tokenisation', 'tokenization'],
]

/** Total cost line → ⓘ copy, first match wins (VAT before the platform fee). */
const COST_LINE_INFO: readonly { token: string; infoKey: CalculatorFeeInfoKey }[] = [
  { token: 'platformfeevat', infoKey: 'platformFeeVat' },
  { token: 'acquisitioncost', infoKey: 'acquisitionCost' },
  { token: 'dld', infoKey: 'dldFee' },
  { token: 'titledeed', infoKey: 'titleDeedFee' },
  { token: 'noc', infoKey: 'nocFee' },
  { token: 'platformfee', infoKey: 'platformFee' },
]

/** Acquisition Cost sheet row → ⓘ copy. */
const ACQUISITION_ROW_INFO: readonly {
  tokens: readonly string[]
  infoKey: CalculatorFeeInfoKey
}[] = [
  { tokens: ['brokerage'], infoKey: 'brokerageFee' },
  { tokens: ['trustee'], infoKey: 'trusteeFee' },
  { tokens: ['valuation'], infoKey: 'valuationFee' },
  { tokens: ['conveyancing'], infoKey: 'conveyancingFee' },
  { tokens: ['utilit'], infoKey: 'utilitiesConnectionFee' },
  { tokens: ['furnish', 'furniture'], infoKey: 'furnishingBudget' },
  { tokens: ['renovation', 'refurbish'], infoKey: 'renovationCost' },
  { tokens: ['maintenance'], infoKey: 'maintenanceReserve' },
  { tokens: ['insurance'], infoKey: 'propertyInsuranceCost' },
  { tokens: ['tokenisation', 'tokenization'], infoKey: 'tokenisationFee' },
]

const normaliseFeeToken = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const feeToken = (fee: Pick<OrderFeeLine, 'code' | 'label'>): string =>
  normaliseFeeToken(`${fee.code}${fee.label}`)

/** The ⓘ copy for a Total cost line, or undefined where the app shows none. */
export const costLineInfoFor = (token: string): FeeInfo | undefined => {
  const match = COST_LINE_INFO.find((entry) => normaliseFeeToken(token).includes(entry.token))
  return match ? CALCULATOR_FEE_INFO[match.infoKey] : undefined
}

/** The ⓘ copy for an Acquisition Cost sheet row, or undefined where the app shows none. */
export const acquisitionRowInfoFor = (token: string): FeeInfo | undefined => {
  const normalised = normaliseFeeToken(token)
  const match = ACQUISITION_ROW_INFO.find((entry) =>
    entry.tokens.some((one) => normalised.includes(one)),
  )
  return match ? CALCULATOR_FEE_INFO[match.infoKey] : undefined
}

const rankIn = (order: readonly (readonly string[])[], fee: OrderFeeLine): number => {
  const token = feeToken(fee)
  return order.findIndex((aliases) => aliases.some((alias) => token.includes(alias)))
}

/** `fees` sorted by `order`, unmatched ones last and in their own order. */
const sortBy = (order: readonly (readonly string[])[], fees: OrderFeeLine[]): OrderFeeLine[] => {
  const rankOf = (fee: OrderFeeLine) => {
    const rank = rankIn(order, fee)
    return rank === -1 ? order.length : rank
  }
  return fees
    .map((fee, index) => ({ fee, index }))
    .sort((a, b) => rankOf(a.fee) - rankOf(b.fee) || a.index - b.index)
    .map(({ fee }) => fee)
}

const formatRatePct = (value: number): string => `${parseFloat(value.toFixed(MAX_RATE_DECIMALS))}%`

// Half a fil — anything closer than this is the same amount after rounding.
const ROUNDING_TOLERANCE = 0.005

/** The Acquisition Cost sheet heading — names the VAT rate when every charged row shares it. */
const acquisitionSectionTitle = (fees: OrderFeeLine[], grossOf: (fee: OrderFeeLine) => number) => {
  const rates = new Set(
    fees
      .filter((fee) => fee.vatAmount > 0)
      .map((fee) => {
        const net = grossOf(fee) - fee.vatAmount
        return net > 0 ? formatRatePct((fee.vatAmount / net) * PERCENT_BASE) : null
      }),
  )
  if (rates.size === 0) return 'Fees'
  const [only] = [...rates]
  return rates.size === 1 && only
    ? `Fees including ${only} VAT on each`
    : 'Fees including VAT on each'
}

/**
 * The server quote's fee lines as the app's Total cost well lists them. When
 * the lines already add up to the quoted total, `amount` is VAT-inclusive;
 * otherwise the line's VAT is added to it.
 */
export const buildCalculatorFeeRows = (
  quote: Pick<CalculateFees, 'fees' | 'totalFeeAmount'> | null | undefined,
): CalculatorFeeLine[] => {
  if (!quote) return []
  const sum = quote.fees.reduce((total, fee) => total + fee.amount, 0)
  const isVatIncluded = Math.abs(sum - quote.totalFeeAmount) < ROUNDING_TOLERANCE
  const grossOf = (fee: OrderFeeLine) => (isVatIncluded ? fee.amount : fee.amount + fee.vatAmount)
  const captionOf = (fee: OrderFeeLine) => (fee.rate ? `${fee.label} (${fee.rate})` : fee.label)

  const visible = quote.fees.filter(
    (fee) => !HIDDEN_FEE_TOKENS.some((hidden) => feeToken(fee).includes(hidden)),
  )
  const platform = visible.find((fee) => feeToken(fee).includes(PLATFORM_FEE_TOKEN))
  const rest = visible.filter((fee) => fee !== platform)
  const isInline = (fee: OrderFeeLine) => rankIn(INLINE_ORDER, fee) !== -1

  const lines: CalculatorFeeLine[] = sortBy(INLINE_ORDER, rest.filter(isInline)).map((fee) => ({
    key: fee.code,
    label: captionOf(fee),
    value: formatAed(grossOf(fee)),
    info: costLineInfoFor(feeToken(fee)),
  }))

  if (platform) {
    const vat = platform.vatAmount
    const net = grossOf(platform) - vat
    lines.push({
      key: platform.code,
      label: captionOf(platform),
      value: formatAed(net),
      info: costLineInfoFor(PLATFORM_FEE_TOKEN),
    })
    if (vat > 0) {
      lines.push({
        key: PLATFORM_FEE_VAT_KEY,
        label:
          net > 0
            ? `${PLATFORM_FEE_VAT_LABEL} (${formatRatePct((vat / net) * PERCENT_BASE)})`
            : PLATFORM_FEE_VAT_LABEL,
        value: formatAed(vat),
        info: costLineInfoFor(PLATFORM_FEE_VAT_KEY),
      })
    }
  }

  const acquisitionFees = sortBy(
    ACQUISITION_ORDER,
    rest.filter((fee) => !isInline(fee)),
  )
  if (acquisitionFees.length) {
    const total = acquisitionFees.reduce((acc, fee) => acc + grossOf(fee), 0)
    lines.push({
      key: ACQUISITION_COST_KEY,
      label: ACQUISITION_COST_LABEL,
      value: formatAed(total),
      info: costLineInfoFor(ACQUISITION_COST_KEY),
      acquisition: {
        sectionTitle: acquisitionSectionTitle(acquisitionFees, grossOf),
        rows: acquisitionFees.map((fee) => ({
          key: fee.code,
          label: captionOf(fee),
          value: grossOf(fee) === 0 ? NIL_AMOUNT : formatAed(grossOf(fee)),
          info: acquisitionRowInfoFor(feeToken(fee)),
        })),
      },
    })
  }

  return lines
}
